import React, { useState, SyntheticEvent, ReactElement, useContext } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';
import { ForbiddenError, HttpClient, NotFoundError } from '../../api/HttpClient';
import { useAlert } from '../../contexts/alert/useAlert';
import { LoadingButton } from '../../components/LoadingButton';
import { InputValidatorMode, useUserNameValidator } from '../../contexts/useInputValidator';
import { RetroRoomResponse } from '../../api/Requests';
import { Session, SessionStorage } from '../../localstorage/SessionStorage';
import { ContextPlayer, PlayerContext } from '../../contexts/PlayerContext';
import { UserContext } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { RetroCategory, RetroAction } from './analytics/RetroTracking';
import { useTracking } from '../../analytics/useTracking';
import { RetroBetaBanner } from './RetroBetaBanner';

export interface JoinRetroRoomProps {
  roomUrlId: string;
}

export default function JoinRetroRoom({ roomUrlId }: JoinRetroRoomProps): ReactElement {
  const [validateUserName] = useUserNameValidator();
  const [playerName, setPlayerName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const alert = useAlert();
  const { setPlayer } = useContext(PlayerContext);
  const { authToken } = useContext(UserContext);
  const { track } = useTracking();
  const navigate = useNavigate();
  const sessionStorage = new SessionStorage();

  function validateInput(value: string, mode: InputValidatorMode) {
    const valid = validateUserName(value, mode).isValid;
    setIsValid(valid);
    return valid;
  }

  function handleOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    setPlayerName(event.target.value);
    validateInput(event.target.value, InputValidatorMode.onTyping);
  }

  async function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    if (!validateInput(playerName, InputValidatorMode.onSubmit)) return;

    setIsLoading(true);
    const httpClient = new HttpClient(authToken);
    try {
      const response = await httpClient.post('/retro-rooms/join', {
        roomUrlId,
        playerName,
      }) as RetroRoomResponse;

      const player = response.players.find(
        p => p.name.toLowerCase() === playerName.toLowerCase()
      );
      if (player) {
        track.event(RetroCategory.Room, RetroAction.RoomJoined);
        sessionStorage.SaveSession(new Session(response.urlId, player.id, playerName));
        setPlayer(new ContextPlayer(player.id, playerName, player.isModerator));
        navigate(`/retro/${response.urlId}`);
      } else {
        setIsLoading(false);
        alert.showMessage('An unexpected error occurred, please try again', 'error');
      }
    } catch (e) {
      setIsLoading(false);
      let errorMessage = 'Not able to join retro room, please try again';
      if (e instanceof ForbiddenError) {
        errorMessage = 'This retrospective is no longer open for new participants. If you joined while signed in, sign in with that account and open it from Recent Retros. If you joined without an account, open this link in the same browser you used last time.';
      } else if (e instanceof NotFoundError) {
        errorMessage = `Retro room '${roomUrlId}' does not exist`;
      }
      alert.showMessage(errorMessage, 'error');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container sx={{ padding: 3 }} spacing={3} direction="column" justifyContent="space-between" alignItems="center">
        <Grid>
          <Typography align="center" variant="h5">Join the retrospective room</Typography>
          <Typography align="center" variant="subtitle1">and collaborate with your team on sticky notes</Typography>
        </Grid>
        <Grid>
          <TextField
            autoComplete="nickname"
            error={!isValid}
            helperText={validateUserName(playerName, InputValidatorMode.onTyping).errorMessage}
            disabled={isLoading}
            onChange={handleOnChange}
            label="Please enter your name"
            variant="outlined"
            color="secondary"
          />
        </Grid>
        <Grid>
          <LoadingButton type="submit" isLoading={isLoading}>Join Retro Room</LoadingButton>
        </Grid>
        <Grid sx={{ width: '100%', maxWidth: 480 }}>
          <RetroBetaBanner />
        </Grid>
      </Grid>
    </form>
  );
}
